// Editor de landing pages em tela cheia: blocos, tema e configurações à esquerda, prévia ao vivo à direita.
// Salva sozinho (rascunho); "Publicar" coloca a versão atual no ar.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowDown, ArrowLeft, ArrowUp, Check, CloudOff, Copy, ExternalLink, Eye, EyeOff, GripVertical, Layers, Loader2, Monitor, Palette as PaletteIcon,
  Pencil, Plus, Redo2, Rocket, Settings2, Smartphone, Trash2, Undo2, X,
} from 'lucide-react'
import type { LandingPageDto } from '@impulso/shared/api/tracking'
import {
  BLOCK_CATALOG, blockId, LANDING_FONTS, LANDING_LIMITS, type LandingBlock, type LandingBlockType, type LandingContent, type LandingFont, newBlock, THEME_PRESETS,
} from '@impulso/shared/landing'
import { errorMessage } from '../../../lib/api'
import { useCan, useCompany } from '../../../lib/company'
import { ErrorState, Loading } from '../../../ui'
import { useConfirm } from '../../../ui/confirm'
import { useToast } from '../../../ui/toast'
import { useAssets } from '../../../features/assets/api'
import { useLandingAction, useLandingPage, useLandingPages, useUpdateLanding } from '../../../features/tracking/api'
import { LandingAddonCard } from '../../../features/tracking/components'
import BlockForm from '../../../features/landing/BlockForm'
import Preview from '../../../features/landing/Preview'
import { AreaField, Choice, Field, ImageField, TextField, Toggle } from '../../../features/landing/fields'
import '../../../features/landing/editor.css'
import '../../../features/tracking/tracking.css'

export default function LandingEditor() {
  const company = useCompany()
  const { pageId } = useParams()
  const q = useLandingPage(company.id, pageId!)
  if (q.isLoading) return <Loading lines={6} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  return <Editor key={q.data!.id} initial={q.data!} />
}

type SaveState = 'saved' | 'dirty' | 'saving' | 'error'
type Tab = 'blocks' | 'theme' | 'page'

const FONT_LABEL: Record<LandingFont, string> = { inter: 'Inter (moderna)', poppins: 'Poppins (amigável)', montserrat: 'Montserrat (forte)', lora: 'Lora (clássica)', playfair: 'Playfair (elegante)' }

function blockSummary(b: LandingBlock): string {
  switch (b.type) {
    case 'header': return b.name
    case 'hero': case 'text': case 'features': case 'image_text': case 'gallery': case 'testimonials': case 'pricing': case 'faq': case 'cta': case 'form': case 'video': case 'countdown': case 'map':
      return b.title || BLOCK_CATALOG[b.type].description
    case 'stats': return b.items.map((i) => i.value).join(' · ')
    case 'spacer': return b.line ? 'Com linha' : 'Espaço em branco'
    case 'footer': return b.text
  }
}

function cloneBlock(b: LandingBlock): LandingBlock {
  return { ...(JSON.parse(JSON.stringify(b)) as LandingBlock), id: blockId() }
}

function Editor({ initial }: { initial: LandingPageDto }) {
  const company = useCompany()
  const canEdit = useCan('company.edit_drafts')
  const canPublish = useCan('company.publish')
  const toast = useToast()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const pageQ = useLandingPage(company.id, initial.id)
  const page = pageQ.data ?? initial
  const list = useLandingPages(company.id)
  const allowance = list.data?.allowance
  const update = useUpdateLanding(company.id, initial.id)
  const act = useLandingAction(company.id)
  const assets = useAssets(company.id)

  const [content, setContent] = useState<LandingContent>(initial.content)
  const [title, setTitle] = useState(initial.title)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('blocks')
  const [adding, setAdding] = useState(false)
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [showPreview, setShowPreview] = useState(false)
  const [save, setSave] = useState<SaveState>('saved')
  const [addonOpen, setAddonOpen] = useState(false)
  const [, rerender] = useState(0)

  // ---------------------------------------------------------------- histórico (desfazer/refazer)
  const current = useRef(content)
  const hist = useRef<{ past: LandingContent[]; future: LandingContent[]; key: string | null; at: number }>({ past: [], future: [], key: null, at: 0 })
  const apply = (value: LandingContent) => {
    current.current = value
    setContent(value)
    rerender((n) => n + 1)
  }
  const change = (next: LandingContent | ((c: LandingContent) => LandingContent), key: string | null = null) => {
    const cur = current.current
    const value = typeof next === 'function' ? next(cur) : next
    const h = hist.current
    const now = Date.now()
    // digitação seguida no mesmo campo vira um passo só
    if (!(key && h.key === key && now - h.at < 1200)) h.past = [...h.past.slice(-60), cur]
    h.future = []
    h.key = key
    h.at = now
    apply(value)
  }
  const undo = () => {
    const h = hist.current
    const prev = h.past.pop()
    if (!prev) return
    h.future.push(current.current)
    h.key = null
    apply(prev)
  }
  const redo = () => {
    const h = hist.current
    const next = h.future.pop()
    if (!next) return
    h.past.push(current.current)
    h.key = null
    apply(next)
  }
  const keys = useRef({ undo, redo })
  keys.current = { undo, redo }

  // ---------------------------------------------------------------- salvamento automático
  const saved = useRef(JSON.stringify({ c: initial.content, t: initial.title }))
  const snapshot = JSON.stringify({ c: content, t: title })
  const dirty = snapshot !== saved.current
  const saveNow = useCallback(async (): Promise<boolean> => {
    const snap = JSON.stringify({ c: content, t: title })
    if (snap === saved.current) return true
    if (!title.trim()) { setSave('error'); toast('Dê um nome para a página.'); return false }
    setSave('saving')
    try {
      await update.mutateAsync({ content, title: title.trim() })
      saved.current = snap
      setSave('saved')
      return true
    } catch (err) {
      setSave('error')
      toast(errorMessage(err))
      return false
    }
  }, [content, title, update, toast])

  useEffect(() => {
    if (!canEdit) return
    if (!dirty) { setSave((st) => (st === 'dirty' ? 'saved' : st)); return }
    setSave('dirty')
    const t = setTimeout(() => void saveNow(), 1200)
    return () => clearTimeout(t)
  }, [snapshot])

  useEffect(() => {
    const onBefore = (e: BeforeUnloadEvent) => { if (dirty) e.preventDefault() }
    window.addEventListener('beforeunload', onBefore)
    return () => window.removeEventListener('beforeunload', onBefore)
  }, [dirty])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) keys.current.redo(); else keys.current.undo() }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); keys.current.redo() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ---------------------------------------------------------------- blocos
  const urls = useMemo(() => new Map((assets.data?.items ?? []).map((a) => [a.id, a.url])), [assets.data])
  const assetUrl = useCallback((id: string) => urls.get(id) ?? null, [urls])
  const phone = content.whatsappFloat.phone || (() => {
    for (const b of content.blocks) {
      const btn = b.type === 'header' ? b.button : b.type === 'hero' ? b.primary : b.type === 'cta' ? b.button : null
      if (btn && 'phone' in btn.action && btn.action.phone) return btn.action.phone
    }
    return ''
  })()
  const biz = { name: company.name, whatsapp: phone, city: company.city, segment: company.segment }
  const selected = content.blocks.find((b) => b.id === selectedId) ?? null
  const setBlocks = (fn: (bs: LandingBlock[]) => LandingBlock[], key: string | null = null) => change((c) => ({ ...c, blocks: fn(c.blocks) }), key)
  const updateBlock = (b: LandingBlock) => setBlocks((bs) => bs.map((x) => (x.id === b.id ? b : x)), `block:${b.id}`)
  const addBlock = (type: LandingBlockType) => {
    if (content.blocks.length >= LANDING_LIMITS.blocks) return toast(`Uma página pode ter até ${LANDING_LIMITS.blocks} blocos.`)
    const b = newBlock(type, biz)
    setBlocks((bs) => {
      const at = selectedId ? bs.findIndex((x) => x.id === selectedId) + 1 : (bs.at(-1)?.type === 'footer' ? bs.length - 1 : bs.length)
      const next = bs.slice()
      next.splice(at > 0 ? at : bs.length, 0, b)
      return next
    })
    setSelectedId(b.id)
    setAdding(false)
  }
  const moveBlock = (from: number, to: number) => setBlocks((bs) => {
    if (to < 0 || to >= bs.length || from === to) return bs
    const next = bs.slice()
    const [b] = next.splice(from, 1)
    next.splice(to, 0, b!)
    return next
  })
  const select = useCallback((id: string) => { setSelectedId(id); setTab('blocks'); setAdding(false) }, [])

  // ---------------------------------------------------------------- publicação
  async function publish() {
    if (!(await saveNow())) return
    if (allowance && !allowance.active) { setAddonOpen(true); return }
    act.mutate({ id: initial.id, action: 'publish' }, {
      onSuccess: (p) => toast(p?.status === 'published' ? 'Página no ar! Use o endereço no seu anúncio.' : 'Publicado.'),
      onError: (e) => toast(errorMessage(e)),
    })
  }
  async function back() {
    if (dirty && canEdit) await saveNow()
    navigate(`/app/${company.id}/trackeamento/landing-pages`)
  }

  const hasChanges = page.status !== 'published' || page.hasUnpublishedChanges || dirty
  const SaveIcon = save === 'saving' ? Loader2 : save === 'error' ? CloudOff : save === 'dirty' ? Pencil : Check

  return (
    <div className="lpe">
      <header className="lpe-top">
        <button className="icon-btn" aria-label="Voltar para as páginas" onClick={() => void back()}><ArrowLeft size={18} /></button>
        <input className="title-input" aria-label="Nome da página" value={title} maxLength={120} disabled={!canEdit} onChange={(e) => setTitle(e.target.value)} />
        <span className={`badge ${page.status === 'published' ? 'green' : page.status === 'draft' ? 'purple' : ''} hide-sm`}>{page.status === 'published' ? 'No ar' : page.status === 'draft' ? 'Rascunho' : 'Fora do ar'}</span>
        {canEdit && <span className={`lpe-save ${save === 'error' ? 'err' : ''}`} role="status" aria-live="polite"><SaveIcon size={14} className={save === 'saving' ? 'spin' : ''} aria-hidden="true" /><span className="lpe-save-txt">{save === 'saving' ? 'Salvando…' : save === 'error' ? 'Não salvou' : save === 'dirty' ? 'Editando…' : 'Salvo'}</span></span>}
        <div className="grow" />
        {canEdit && <>
          <button className="mini-btn hide-sm" style={{ width: 34, height: 34 }} aria-label="Desfazer" title="Desfazer (Ctrl+Z)" disabled={!hist.current.past.length} onClick={undo}><Undo2 size={17} /></button>
          <button className="mini-btn hide-sm" style={{ width: 34, height: 34 }} aria-label="Refazer" title="Refazer (Ctrl+Shift+Z)" disabled={!hist.current.future.length} onClick={redo}><Redo2 size={17} /></button>
        </>}
        <div className="tabs hide-sm" role="group" aria-label="Tamanho da prévia">
          <button className={`tab ${device === 'desktop' ? 'on' : ''}`} aria-pressed={device === 'desktop'} onClick={() => setDevice('desktop')} title="Computador"><Monitor size={15} /></button>
          <button className={`tab ${device === 'mobile' ? 'on' : ''}`} aria-pressed={device === 'mobile'} onClick={() => setDevice('mobile')} title="Celular"><Smartphone size={15} /></button>
        </div>
        <button className="btn sm lpe-mobile-switch" onClick={() => setShowPreview(!showPreview)}>{showPreview ? <><Pencil size={14} /> Editar</> : <><Eye size={14} /> Prévia</>}</button>
        {page.status === 'published' && <a className="btn sm hide-sm" href={page.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Ver no ar</a>}
        {canPublish && (
          <button className="btn primary sm" disabled={act.isPending || save === 'saving' || !hasChanges} onClick={() => void publish()}>
            <Rocket size={14} /> {act.isPending ? 'Publicando…' : page.status === 'published' ? (hasChanges ? 'Publicar alterações' : 'Publicado') : 'Publicar'}
          </button>
        )}
      </header>

      <div className={`lpe-body ${showPreview ? 'show-preview' : ''}`}>
        <aside className="lpe-panel">
          <div className="lpe-tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'blocks'} className={tab === 'blocks' ? 'on' : ''} onClick={() => setTab('blocks')}><Layers size={15} /> Blocos</button>
            <button role="tab" aria-selected={tab === 'theme'} className={tab === 'theme' ? 'on' : ''} onClick={() => setTab('theme')}><PaletteIcon size={15} /> Visual</button>
            <button role="tab" aria-selected={tab === 'page'} className={tab === 'page' ? 'on' : ''} onClick={() => setTab('page')}><Settings2 size={15} /> Página</button>
          </div>
          <div className="lpe-scroll">
            {!canEdit && <div className="callout small">Você pode ver, mas não editar esta página.</div>}
            {tab === 'blocks' && (adding
              ? <BlockPalette onPick={addBlock} onClose={() => setAdding(false)} />
              : selected
                ? (
                  <>
                    <div className="row between">
                      <button className="btn ghost sm" onClick={() => setSelectedId(null)}><ArrowLeft size={14} /> Todos os blocos</button>
                      <span className="badge purple">{BLOCK_CATALOG[selected.type].label}</span>
                    </div>
                    <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                      <BlockForm block={selected} onChange={updateBlock} companyId={company.id} canEdit={canEdit} phone={phone} />
                    </fieldset>
                  </>
                )
                : <BlockList blocks={content.blocks} selectedId={selectedId} canEdit={canEdit} onSelect={select} onMove={moveBlock} onAdd={() => setAdding(true)}
                    onToggle={(id) => setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, hidden: !b.hidden } : b)))}
                    onDuplicate={(id) => setBlocks((bs) => { const i = bs.findIndex((b) => b.id === id); const next = bs.slice(); next.splice(i + 1, 0, cloneBlock(bs[i]!)); return next })}
                    onRemove={async (id) => { if (await confirm({ title: 'Remover este bloco?', confirmLabel: 'Remover', danger: true })) setBlocks((bs) => bs.filter((b) => b.id !== id)) }} />)}
            {tab === 'theme' && <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}><ThemePanel content={content} onChange={change} /></fieldset>}
            {tab === 'page' && <PagePanel page={page} content={content} canEdit={canEdit} canPublish={canPublish} onChange={change} />}
          </div>
        </aside>
        <div className="lpe-stage">
          <Preview src={`/api/v1/companies/${company.id}/landing-pages/${initial.id}/preview`} content={content} assetUrl={assetUrl} selectedId={selectedId} device={device} onSelect={select} pageTitle={title} />
        </div>
      </div>

      {addonOpen && allowance && (
        <div className="modal-backdrop" onClick={() => setAddonOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-label="Ativar páginas" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h2>Falta só ativar o adicional</h2><button className="icon-btn" style={{ marginLeft: 'auto' }} aria-label="Fechar" onClick={() => setAddonOpen(false)}><X size={17} /></button></div>
            <div className="modal-body">
              <p className="small muted">Sua página está salva. Para colocá-la no ar, ative o adicional de Páginas.</p>
              <LandingAddonCard allowance={allowance} />
              {allowance.active && <button className="btn primary" onClick={() => { setAddonOpen(false); void publish() }}><Rocket size={15} /> Publicar agora</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function BlockList({ blocks, selectedId, canEdit, onSelect, onMove, onAdd, onToggle, onDuplicate, onRemove }: {
  blocks: LandingBlock[]; selectedId: string | null; canEdit: boolean; onSelect: (id: string) => void; onMove: (from: number, to: number) => void; onAdd: () => void
  onToggle: (id: string) => void; onDuplicate: (id: string) => void; onRemove: (id: string) => void
}) {
  const [drag, setDrag] = useState<number | null>(null)
  const [over, setOver] = useState<number | null>(null)
  return (
    <>
      <p className="small muted">Clique em um bloco (aqui ou na prévia) para editar. Arraste para mudar a ordem.</p>
      <div className="stack" style={{ gap: 6 }}>
        {blocks.map((b, i) => (
          <div key={b.id} className={`block-row ${b.id === selectedId ? 'on' : ''} ${b.hidden ? 'hidden-block' : ''} ${over === i && drag !== null && drag !== i ? 'drag-over' : ''}`}
            draggable={canEdit} onClick={() => onSelect(b.id)}
            onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = 'move' }}
            onDragOver={(e) => { if (drag === null) return; e.preventDefault(); setOver(i) }}
            onDragLeave={() => setOver((o) => (o === i ? null : o))}
            onDrop={(e) => { e.preventDefault(); if (drag !== null) onMove(drag, i); setDrag(null); setOver(null) }}
            onDragEnd={() => { setDrag(null); setOver(null) }}>
            <span className="grip" aria-hidden><GripVertical size={16} /></span>
            <div className="meta">
              <div className="t">{BLOCK_CATALOG[b.type].label}{b.hidden ? ' (oculto)' : ''}</div>
              <div className="s">{blockSummary(b)}</div>
            </div>
            {canEdit && (
              <span className="row" style={{ gap: 0 }} onClick={(e) => e.stopPropagation()}>
                <button className="mini-btn" aria-label="Subir" disabled={i === 0} onClick={() => onMove(i, i - 1)}><ArrowUp size={14} /></button>
                <button className="mini-btn" aria-label="Descer" disabled={i === blocks.length - 1} onClick={() => onMove(i, i + 1)}><ArrowDown size={14} /></button>
                <button className="mini-btn" aria-label={b.hidden ? 'Mostrar' : 'Ocultar'} title={b.hidden ? 'Mostrar' : 'Ocultar'} onClick={() => onToggle(b.id)}>{b.hidden ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                <button className="mini-btn" aria-label="Duplicar" title="Duplicar" onClick={() => onDuplicate(b.id)}><Copy size={14} /></button>
                <button className="mini-btn" aria-label="Remover" title="Remover" onClick={() => onRemove(b.id)}><Trash2 size={14} /></button>
              </span>
            )}
          </div>
        ))}
      </div>
      {blocks.length === 0 && <p className="small muted">A página está vazia. Adicione o primeiro bloco.</p>}
      {canEdit && <button className="btn primary" onClick={onAdd}><Plus size={16} /> Adicionar bloco</button>}
    </>
  )
}

/** Paleta de blocos agrupada. */
function BlockPalette({ onPick, onClose }: { onPick: (t: LandingBlockType) => void; onClose: () => void }) {
  const groups = ['Topo', 'Conteúdo', 'Vendas', 'Contato', 'Layout'] as const
  return (
    <>
      <div className="row between"><h3>Adicionar bloco</h3><button className="btn ghost sm" onClick={onClose}><X size={14} /> Cancelar</button></div>
      {groups.map((g) => (
        <div key={g} className="stack" style={{ gap: 8 }}>
          <div className="nav-section" style={{ padding: 0 }}>{g}</div>
          <div className="palette">
            {(Object.keys(BLOCK_CATALOG) as LandingBlockType[]).filter((t) => BLOCK_CATALOG[t].group === g).map((t) => (
              <button key={t} type="button" onClick={() => onPick(t)}><span className="t">{BLOCK_CATALOG[t].label}</span><span className="d">{BLOCK_CATALOG[t].description}</span></button>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

function ThemePanel({ content, onChange }: { content: LandingContent; onChange: (c: LandingContent, key?: string) => void }) {
  const t = content.theme
  const setTheme = (patch: Partial<LandingContent['theme']>, key?: string) => onChange({ ...content, theme: { ...t, ...patch } }, key)
  return (
    <>
      <div className="field">
        <label>Combinações prontas</label>
        <div className="swatches">
          {THEME_PRESETS.map((p) => {
            const on = p.theme.primary === t.primary && p.theme.background === t.background && p.theme.text === t.text
            return (
              <button key={p.name} type="button" className={`swatch ${on ? 'on' : ''}`} onClick={() => setTheme(p.theme)}>
                <span className="dots"><i style={{ background: p.theme.primary }} /><i style={{ background: p.theme.background }} /><i style={{ background: p.theme.text }} /></span>
                {p.name}
              </button>
            )
          })}
        </div>
      </div>
      <div className="field">
        <label>Cores</label>
        {([['primary', 'Cor principal (botões e destaques)'], ['background', 'Fundo'], ['text', 'Texto']] as const).map(([k, label]) => (
          <div key={k} className="color-row">
            <input type="color" aria-label={label} value={t[k]} onChange={(e) => setTheme({ [k]: e.target.value }, `theme:${k}`)} />
            <span className="small">{label}</span>
            <code className="small muted" style={{ marginLeft: 'auto' }}>{t[k]}</code>
          </div>
        ))}
      </div>
      <Field label="Fonte">
        <select className="input" value={t.font} onChange={(e) => setTheme({ font: e.target.value as LandingFont })}>
          {LANDING_FONTS.map((f) => <option key={f} value={f}>{FONT_LABEL[f]}</option>)}
        </select>
      </Field>
      <Choice label="Cantos" value={t.radius} onChange={(v) => setTheme({ radius: v })} options={[{ value: 'square', label: 'Retos' }, { value: 'soft', label: 'Suaves' }, { value: 'round', label: 'Redondos' }]} />
    </>
  )
}

function PagePanel({ page, content, canEdit, canPublish, onChange }: { page: LandingPageDto; content: LandingContent; canEdit: boolean; canPublish: boolean; onChange: (c: LandingContent, key?: string) => void }) {
  const company = useCompany()
  const update = useUpdateLanding(company.id, page.id)
  const act = useLandingAction(company.id)
  const toast = useToast()
  const confirm = useConfirm()
  const [slug, setSlug] = useState(page.slug)
  useEffect(() => setSlug(page.slug), [page.slug])
  const isDomainHome = !page.url.endsWith(`/${page.slug}`)
  const base = isDomainHome ? `${page.url}` : page.url.slice(0, page.url.length - page.slug.length)
  const seo = content.seo
  const wa = content.whatsappFloat
  return (
    <>
      <div className="field">
        <label htmlFor="slug">Endereço da página</label>
        <div className="row" style={{ gap: 0 }}>
          <span className="small muted" style={{ whiteSpace: 'nowrap', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }} title={base}>{base}</span>
          <input id="slug" className="input" value={slug} disabled={!canEdit} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} maxLength={60} />
        </div>
        {canEdit && slug !== page.slug && (
          <div className="row" style={{ gap: 8 }}>
            <button className="btn sm primary" disabled={update.isPending} onClick={() => update.mutate({ slug }, { onSuccess: () => toast('Endereço atualizado.'), onError: (e) => toast(errorMessage(e)) })}>Salvar endereço</button>
            <button className="btn sm ghost" onClick={() => setSlug(page.slug)}>Cancelar</button>
          </div>
        )}
        {isDomainHome && <span className="small muted">Esta é a página principal do domínio {page.url}. O endereço abaixo vale para o link alternativo.</span>}
        {page.status === 'published' && <span className="small muted">Mudar o endereço quebra links que já foram divulgados (ex.: anúncios ativos).</span>}
      </div>
      <fieldset disabled={!canEdit} className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 14, minWidth: 0 }}>
        <div className="divider" style={{ margin: 0 }} />
        <h3>Botão flutuante do WhatsApp</h3>
        <Toggle label="Mostrar no canto da tela" value={wa.enabled} onChange={(v) => onChange({ ...content, whatsappFloat: { ...wa, enabled: v } })} />
        {wa.enabled && <>
          <TextField label="Número" inputMode="tel" placeholder="(11) 99999-9999" value={wa.phone} max={30} onChange={(v) => onChange({ ...content, whatsappFloat: { ...wa, phone: v } }, 'wa:phone')} />
          <AreaField label="Mensagem que já vem escrita" rows={2} max={300} value={wa.message} onChange={(v) => onChange({ ...content, whatsappFloat: { ...wa, message: v } }, 'wa:msg')} />
        </>}
        <div className="divider" style={{ margin: 0 }} />
        <h3>Google e compartilhamento</h3>
        <TextField label="Título da aba / do Google" value={seo.title} max={120} onChange={(v) => onChange({ ...content, seo: { ...seo, title: v } }, 'seo:title')} hint={`${seo.title.length}/60 recomendados`} />
        <AreaField label="Descrição" rows={3} max={300} value={seo.description} onChange={(v) => onChange({ ...content, seo: { ...seo, description: v } }, 'seo:desc')} hint="Aparece no Google e quando alguém compartilha o link no WhatsApp." />
        <ImageField label="Imagem ao compartilhar" companyId={company.id} canEdit={canEdit} value={seo.image} onChange={(v) => onChange({ ...content, seo: { ...seo, image: v } })} />
      </fieldset>
      <div className="divider" style={{ margin: 0 }} />
      <h3>Rastreamento</h3>
      <div className="callout small"><span>Esta página já sai com a tag do Seu Impulso: conta visitas, cliques no WhatsApp e contatos do formulário, e avisa o Pixel da Meta e a tag do Google configurados em <Link className="strong" to={`/app/${company.id}/trackeamento/eventos`}>Eventos e conversões</Link>.</span></div>
      {canPublish && page.status === 'published' && (
        <>
          <div className="divider" style={{ margin: 0 }} />
          <button className="btn" style={{ color: 'var(--red)' }} disabled={act.isPending} onClick={async () => { if (await confirm({ title: 'Tirar a página do ar?', confirmLabel: 'Tirar do ar', danger: true })) act.mutate({ id: page.id, action: 'unpublish' }, { onSuccess: () => toast('Página fora do ar.'), onError: (e) => toast(errorMessage(e)) }) }}>Tirar a página do ar</button>
        </>
      )}
    </>
  )
}
